<template>
  <v-container fluid class="fill-height bg-grey-lighten-4">
    <v-row justify="center" align="center" class="fill-height">
      <v-col cols="12" sm="10" md="8" lg="9" xl="7">
        <v-card elevation="8" class="mx-auto mb-4" style="border-top: 8px solid var(--color-orange-primary);">
          <v-card-text class="pa-8">
            <!-- Logo and Header -->
            <div class="text-center mb-8">
              <router-link to="/">
                <v-img 
                  src="https://ia-link.com/logogallopulo" 
                  alt="Logo Estudio Gallo Puló" 
                  max-height="60"
                  max-width="200"
                  class="mx-auto mb-4"
                />
              </router-link>
              <h2 class="text-h4 font-heading text-dark-charcoal mb-2">Acceso de Clientes</h2>
              <p class="text-body-2 text-grey-darken-1">
                Diseñamos una herramienta única para nuestros clientes: acceso las 24 horas, los 7 días de la semana a su documentación clave.
              </p>
            </div>

            <v-row>
              <!-- Left Column: Form and Benefits -->
              <v-col cols="12" md="6" class="border-e-md">
                <h3 class="text-h6 font-weight-bold text-center mb-4">Ingrese a su portal</h3>
                
                <v-form @submit.prevent="handleLogin">
                  <v-text-field
                    v-model="loginForm.cuit"
                    label="CUIT"
                    placeholder="XX-XXXXXXXX-X"
                    variant="outlined"
                    class="mb-4"
                    color="orange"
                  />
                  
                  <v-text-field
                    v-model="loginForm.password"
                    label="Contraseña"
                    type="password"
                    placeholder="••••••••"
                    variant="outlined"
                    class="mb-6"
                    color="orange"
                  />
                  
                  <v-btn
                    type="submit"
                    :loading="loading"
                    :disabled="loading"
                    color="orange"
                    size="large"
                    block
                    class="mb-6"
                    elevation="2"
                  >
                    Ingresar
                  </v-btn>
                </v-form>

                <!-- Benefits -->
                <v-card variant="tonal" color="orange">
                  <template v-slot:text>
                    <v-list density="compact">
                      <v-list-item color="bg-transparent" variant="flat">
                        <template v-slot:prepend>
                          <v-icon
                            color="orange"
                            size="small"
                            icon="mdi-check-bold"
                          ></v-icon>
                        </template>
                        <v-list-item-title class="text-body-2 text-wrap"
                          >Documentación siempre actualizada</v-list-item-title
                        >
                      </v-list-item>
                      <v-list-item color="bg-transparent" variant="flat">
                        <template #prepend>
                          <v-icon
                            color="orange"
                            size="small"
                            icon="mdi-check-bold"
                          ></v-icon>
                        </template>
                        <v-list-item-title class="text-body-2 text-wrap"
                          >Acceso 100% seguro con usuario y clave
                          personal</v-list-item-title
                        >
                      </v-list-item>
                      <v-list-item color="bg-transparent" variant="flat">
                        <template #prepend>
                          <v-icon
                            color="orange"
                            size="small"
                            icon="mdi-check-bold"
                          ></v-icon>
                        </template>
                        <v-list-item-title class="text-body-2 text-wrap">
                          Ideal para trámites bancarios, solicitud de créditos, tarjetas,
                          etc.
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </template>
                </v-card>
              </v-col>

              <!-- Right Column: Documentation -->
              <v-col cols="12" md="6">
                <h3 class="text-h6 font-weight-bold text-center mb-4">Documentación Disponible</h3>
                
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-list density="compact">
                      <v-list-subheader class="text-subtitle-1 font-weight-bold text-brand-brown mb-2">
                        De los Socios
                      </v-list-subheader>
                      <v-list-item
                        v-for="doc in sociosDocuments"
                        :key="doc"
                        class="px-0 py-1"
                      >
                        <template v-slot:prepend>
                          <v-icon icon="mdi-circle-medium" size="medium" class="me-0"></v-icon>
                        </template>
                        <v-list-item-title class="text-body-2 text-wrap">{{ doc }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-col>
                  
                  <v-col cols="12" sm="6">
                    <v-list density="compact">
                      <v-list-subheader class="text-subtitle-1 font-weight-bold text-brand-brown mb-2">
                        De la Empresa
                      </v-list-subheader>
                      <v-list-item
                        v-for="doc in empresaDocuments"
                        :key="doc"
                        class="px-0 py-1"
                      >
                        <template v-slot:prepend>
                          <v-icon icon="mdi-circle-medium" size="medium" class="me-0"></v-icon>
                        </template>
                        <v-list-item-title class="text-body-2 text-wrap">{{ doc }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>

                <v-alert 
                  type="info" 
                  variant="tonal" 
                  color="orange" 
                  class="mt-6 text-center"
                  density="compact"
                >
                  <em>"Acceso seguro a sus documentos clave, 24/7. Sin llamadas. Sin esperas."</em>
                </v-alert>
              </v-col>
            </v-row>

            <!-- Back to Home -->
            <div class="text-center mt-6">
              <v-btn
                to="/"
                variant="text"
                color="grey-darken-1"
                prepend-icon="mdi-arrow-left"
              >
                Volver al inicio
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

const store = useStore()
const router = useRouter()

const loginForm = ref({
  cuit: '',
  password: ''
})

const loading = ref(false)
const errorMessage = ref('')

const sociosDocuments = [
  'D.N.I.',
  'Títulos de automotores',
  'Cédulas Parcelarias',
  'DDJJ Ganancias Persona Física',
  'Manifestaciones de Bienes'
]

const empresaDocuments = [
  'Balances',
  'DDJJ Ganancias',
  'Actas (Reunión, Directorio, etc.)',
  'Títulos de automotores',
  'Cédulas Parcelarias',
  'DDJJ IVA',
  'DDJJ Empleador F-931'
]

const handleLogin = async () => {
  loading.value = true
  errorMessage.value = ''
  
  try {
    const result = await store.dispatch('login', {
      cuit: loginForm.value.cuit,
      password: loginForm.value.password
    })
    
    if (result.success) {
      // Redirect based on user role
      if (result.role === 'admin') {
        router.push('/admin')
      } else if (result.role === 'user') {
        router.push('/usuarios')
      }
    } else {
      errorMessage.value = result.message || 'Credenciales inválidas'
    }
  } catch (error) {
    errorMessage.value = 'Error al iniciar sesión. Por favor, intente nuevamente.'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
/* Component-specific styles if needed */
</style>
